import { useAsyncAction } from "../../hooks/useAsyncAction";
import { useCallback, useEffect, useState } from "react";
import { Check, Circle, LoaderCircle, Rocket } from "lucide-react";
import type { SetupGuideStatus } from "@io-spoke/contracts/setupGuide";
import { Button } from "../ui/button";
import { SidebarMenuButton, SidebarMenuItem } from "../ui/sidebar";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "../ui/sheet";
import { getSetupGuideStatus } from "../../lib/setupGuide";

type SetupGuideProps = {
  orgId?: string;
  navigate: (route: string) => void;
};

export function SetupGuide(props: SetupGuideProps) {
  return <WorkspaceSetupGuide key={props.orgId} {...props} />;
}

function WorkspaceSetupGuide({ navigate }: SetupGuideProps) {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<SetupGuideStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const loadAction = useAsyncAction();
  const load = useCallback(() => loadAction.run(async () => {
    setLoading(true);

    try {
      setStatus(await getSetupGuideStatus());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load setup progress.");
    } finally {
      setLoading(false);
    }
  }), [loadAction.run]);

  useEffect(() => { void load(); }, [load]);

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (nextOpen) void load();
  };
  const nextStep = status?.steps.find((step) => !step.completed);
  const percentage = status ? Math.round((status.completedSteps / status.totalSteps) * 100) : 0;

  return (
    <>
      <SidebarMenuItem>
        <SidebarMenuButton tooltip="Setup guide" aria-label={status ? `Setup guide · ${status.completedSteps}/${status.totalSteps}` : "Setup guide"} onClick={() => handleOpenChange(true)}>
          {status?.completed ? <Check /> : <Rocket />}
          <span className="group-data-[collapsible=icon]:hidden">{status ? `Setup guide · ${status.completedSteps}/${status.totalSteps}` : "Setup guide"}</span>
        </SidebarMenuButton>
      </SidebarMenuItem>
      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-md">
          <SheetHeader className="text-left">
            <SheetTitle>Set up IO Spoke</SheetTitle>
            <SheetDescription>Get from a new workspace to your first evidence-backed, assigned obligation.</SheetDescription>
          </SheetHeader>
          {loading && status ? <p role="status" className="mt-4 text-sm text-muted-foreground">Refreshing setup progress…</p> : null}

          {loading && !status && !error ? (
            <div className="mt-8 flex items-center gap-3 text-sm text-muted-foreground" role="status">
              <LoaderCircle className="h-5 w-5 animate-spin" /> Loading setup progress
            </div>
          ) : error && !status ? (
            <div className="mt-8" role="alert">
              <p className="text-sm text-destructive">{error}</p>
              <Button variant="outline" className="mt-4" onClick={() => void load()} loading={loadAction.pending} loadingLabel="Loading setup progress…">Try again</Button>
            </div>
          ) : status ? (
            <div className="mt-8 space-y-7">
              <div>
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium">{status.completed ? "Setup complete" : `${status.completedSteps} of ${status.totalSteps} complete`}</span>
                  <span className="text-muted-foreground">{percentage}%</span>
                </div>
                <div className="mt-3 h-2 overflow-hidden rounded-full bg-muted" role="progressbar" aria-label="Setup progress" aria-valuemin={0} aria-valuemax={100} aria-valuenow={percentage}>
                  <div className="h-full rounded-full bg-primary transition-[width]" style={{ width: `${percentage}%` }} />
                </div>
              </div>

              <ol className="space-y-2">
                {status.steps.map((step) => (
                  <li key={step.key}>
                    <button
                      type="button"
                      className="flex min-h-16 w-full items-start gap-3 rounded-lg border border-border p-3 text-left transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      onClick={() => { setOpen(false); navigate(step.href); }}
                    >
                      {step.completed ? <Check className="mt-0.5 h-5 w-5 shrink-0 text-emerald-400" aria-hidden="true" /> : <Circle className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" aria-hidden="true" />}
                      <span><span className="block text-sm font-medium">{step.title}</span><span className="mt-1 block text-xs leading-5 text-muted-foreground">{step.description}</span></span>
                      <span className="sr-only">{step.completed ? "Completed" : "Not completed"}</span>
                    </button>
                  </li>
                ))}
              </ol>

              {nextStep ? (
                <Button className="w-full" onClick={() => { setOpen(false); navigate(nextStep.href); }}>
                  Continue: {nextStep.title}
                </Button>
              ) : (
                <p className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-4 text-sm text-emerald-800 dark:text-emerald-200">Your workspace is ready to turn connected evidence into accountable action.</p>
              )}
            </div>
          ) : null}
        </SheetContent>
      </Sheet>
    </>
  );
}
